import React from 'react';
import { KpiCardShell } from '~analytics-components/kpi-card-shell';
import { DegradationNote } from '~containers/analytics/kpis/degradation-cause';
import { BoxedTileRow } from '~containers/analytics/kpis/forms/boxed-tile-row';
import { couponsInRegion, sumCouponStock } from './coupon-stock';
import type { CouponStockTotals } from './coupon-stock';
import type { CampaignCouponStockState } from './use-campaign-coupon-stock';

interface CouponStockCardProps {
    title: string;
    description: string;
    pick: (totals: CouponStockTotals) => number;
    state: CampaignCouponStockState;
    provinces: string[];
}

const unlimitedNote = (count: number): string =>
    count === 1
        ? '1 coupon without a set quantity is not counted.'
        : `${count} coupons without a set quantity are not counted.`;

const info = (
    description: string,
    unlimited: number,
    allUnlimited: boolean
) => (
    <div style={{ fontSize: 13, lineHeight: '20px', maxWidth: 240 }}>
        {unlimited > 0 && (
            <div
                style={{
                    marginBottom: 6,
                    ...(allUnlimited && {
                        fontWeight: 600,
                        color: 'var(--Warning, #d48806)',
                    }),
                }}
            >
                {unlimitedNote(unlimited)}
            </div>
        )}
        {description}
    </div>
);

const CouponStockCard: React.FC<CouponStockCardProps> = ({
    title,
    description,
    pick,
    state,
    provinces,
}) => {
    const scoped =
        state.status === 'ready'
            ? couponsInRegion(state.coupons, provinces)
            : [];
    const unlimited = scoped.filter((coupon) => coupon.quantity == null).length;
    const allUnlimited = unlimited > 0 && unlimited === scoped.length;

    return (
        <div data-testid={`card-${title}`}>
            <KpiCardShell
                title={title}
                size="sm"
                info={info(description, unlimited, allUnlimited)}
                infoTrigger="hover"
                infoTone={allUnlimited ? 'warning' : undefined}
                isLoading={state.status === 'loading'}
            >
                {state.status === 'unavailable' && (
                    <DegradationNote reason={state.reason} />
                )}
                {state.status === 'ready' && (
                    <BoxedTileRow
                        highlightIndex={0}
                        tiles={[
                            {
                                value: pick(sumCouponStock(scoped)),
                            },
                        ]}
                    />
                )}
            </KpiCardShell>
        </div>
    );
};

export interface CampaignCouponStockCardsProps {
    state: CampaignCouponStockState;
    provinces: string[];
}

/**
 * Total and Remaining coupons: the two Milestone rewards stock cards. Neither
 * comes from analytics, neither follows the page Period, and both read the
 * same shared config fetch (see `useCampaignCouponStock`).
 */
export const CampaignCouponStockCards: React.FC<CampaignCouponStockCardsProps> = ({
    state,
    provinces,
}) => (
    <>
        <CouponStockCard
            title="Total coupons"
            description="Configured quantity summed across reward option coupons with a set quantity."
            pick={(totals) => totals.total}
            state={state}
            provinces={provinces}
        />
        <CouponStockCard
            title="Remaining coupons"
            description="Configured quantity minus claims, floored at zero, summed across reward option coupons with a set quantity."
            pick={(totals) => totals.remaining}
            state={state}
            provinces={provinces}
        />
    </>
);
